import { cn } from '@/lib/cn';
import { useI18n } from '@/lib/i18n';

export interface ChartSeries {
  key: string;
  label: string;
  values: number[];
  color: string;
}

interface LineChartProps {
  labels: string[];
  series: ChartSeries[];
  ariaLabel: string;
  valueFormatter?: (value: number) => string;
  className?: string;
}

function compactNumber(value: number, language = 'en'): string {
  return new Intl.NumberFormat(language, { notation: 'compact', maximumFractionDigits: 1 }).format(value);
}

export function LineChart({ labels, series, ariaLabel, valueFormatter: valueFormatterProp, className }: LineChartProps) {
  const { t, language } = useI18n();
  const valueFormatter = valueFormatterProp ?? ((value: number) => compactNumber(value, language));
  const width = 960;
  const height = 250;
  const left = 44;
  const right = 18;
  const top = 18;
  const bottom = 38;
  const plotWidth = width - left - right;
  const plotHeight = height - top - bottom;
  const max = Math.max(...series.flatMap((item) => item.values), 1);
  const x = (index: number) => left + (labels.length <= 1 ? plotWidth / 2 : (index / (labels.length - 1)) * plotWidth);
  const y = (value: number) => top + plotHeight - (Math.max(value, 0) / max) * plotHeight;
  const ticks = [0, 0.25, 0.5, 0.75, 1];
  const tickIndexes = labels.length <= 7
    ? labels.map((_, index) => index)
    : Array.from(new Set([0, Math.floor((labels.length - 1) / 4), Math.floor((labels.length - 1) / 2), Math.floor((labels.length - 1) * 3 / 4), labels.length - 1]));

  if (labels.length === 0 || series.length === 0) {
    return <div className={cn('flex h-56 items-center justify-center text-sm text-ink-subtle', className)}>{t('tp.charts.noData')}</div>;
  }

  return (
    <div className={cn('space-y-3', className)}>
      <div className="overflow-x-auto">
        <svg viewBox={`0 0 ${width} ${height}`} className="h-56 min-w-[620px] w-full" role="img" aria-label={ariaLabel}>
          <title>{ariaLabel}</title>
          {ticks.map((ratio) => {
            const value = max * ratio;
            const yPosition = y(value);
            return (
              <g key={ratio}>
                <line x1={left} x2={width - right} y1={yPosition} y2={yPosition} stroke="#E4E8F0" strokeDasharray="3 5" />
                <text x={left - 8} y={yPosition + 4} textAnchor="end" fontSize="11" fill="#8A93A6">{valueFormatter(value)}</text>
              </g>
            );
          })}
          {series.map((item) => (
            <g key={item.key}>
              <polyline
                points={item.values.map((value, index) => `${x(index)},${y(value)}`).join(' ')}
                fill="none"
                stroke={item.color}
                strokeWidth={item.key === series[0]?.key ? 3 : 2.25}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {item.values.map((value, index) => (
                <circle key={`${item.key}-${index}`} cx={x(index)} cy={y(value)} r="3.5" fill={item.color}>
                  <title>{`${item.label}, ${labels[index]}: ${valueFormatter(value)}`}</title>
                </circle>
              ))}
            </g>
          ))}
          {tickIndexes.map((index) => (
            <text key={labels[index]} x={x(index)} y={height - 10} textAnchor="middle" fontSize="11" fill="#8A93A6">
              {labels[index]}
            </text>
          ))}
        </svg>
      </div>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-ink-subtle" aria-hidden="true">
        {series.map((item) => (
          <span key={item.key} className="inline-flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: item.color }} />
            {item.label}
          </span>
        ))}
      </div>
      <table className="sr-only">
        <caption>{ariaLabel}</caption>
        <thead><tr><th>{t('tp.charts.period')}</th>{series.map((item) => <th key={item.key}>{item.label}</th>)}</tr></thead>
        <tbody>{labels.map((label, index) => <tr key={label}><th>{label}</th>{series.map((item) => <td key={item.key}>{valueFormatter(item.values[index] ?? 0)}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}

export interface BarListRow {
  key: string;
  label: string;
  value: number;
  detail?: string;
}

interface BarListProps {
  rows: BarListRow[];
  valueFormatter?: (value: number) => string;
  color?: string;
  emptyLabel?: string;
}

export function BarList({ rows, valueFormatter: valueFormatterProp, color = '#00368F', emptyLabel }: BarListProps) {
  const { t, language } = useI18n();
  const valueFormatter = valueFormatterProp ?? ((value: number) => compactNumber(value, language));
  if (rows.length === 0) return <p className="py-8 text-center text-sm text-ink-subtle">{emptyLabel ?? t('tp.charts.noRecords')}</p>;
  const visibleRows = rows.slice(0, 8);
  const max = Math.max(...visibleRows.map((row) => row.value), 1);
  return (
    <div className="space-y-3">
      {visibleRows.map((row) => (
        <div key={row.key}>
          <div className="mb-1 flex items-baseline justify-between gap-3 text-sm">
            <span className="min-w-0 truncate font-medium text-ink" title={row.label}>{row.label}</span>
            <span className="shrink-0 font-mono text-xs text-ink-muted">{valueFormatter(row.value)}</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-surface-subtle" aria-hidden="true">
            <div className="h-full rounded-full transition-[width] duration-300" style={{ width: `${(row.value / max) * 100}%`, backgroundColor: color }} />
          </div>
          {row.detail && <p className="mt-1 text-xs text-ink-subtle">{row.detail}</p>}
        </div>
      ))}
      {rows.length > visibleRows.length && <p className="pt-1 text-xs text-ink-subtle">{t('tp.charts.showingTop', { shown: visibleRows.length, total: rows.length })}</p>}
    </div>
  );
}
